import { usePostHog } from '@posthog/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import useFeature from '~/hooks/useFeature';

export const useLiveChat = () => {
  const [isDriftLoaded, setIsDriftLoaded] = React.useState(false);
  const [driftFeatureEnabled] = useFeature('drift_livechat');
  const posthog = usePostHog();
  const [isPosthogChatEnabled, setIsPosthogChatEnabled] = React.useState(false);

  const isChatEnabled = useMemo(() => {
    return isPosthogChatEnabled || (isDriftLoaded && driftFeatureEnabled);
  }, [isPosthogChatEnabled, isDriftLoaded, driftFeatureEnabled]);

  const showChat = useCallback(() => {
    if (!isChatEnabled) {
      return;
    }
    if (isPosthogChatEnabled) {
      if (posthog.conversations.isAvailable()) {
        posthog.conversations.show();
        setTimeout(() => {
          const button = document.querySelector(
            '#ph-conversations-widget-container button'
          );
          if (button) {
            (button as HTMLElement).click();
          }
        }, 100);
        return;
      }
    }
    if (isDriftLoaded && driftFeatureEnabled) {
      window.drift?.on('ready', (api) => {
        api.openChat();
      });
    }
  }, [
    isDriftLoaded,
    driftFeatureEnabled,
    isPosthogChatEnabled,
    posthog,
    isChatEnabled,
  ]);

  // posthog chat enabled hook
  useEffect(() => {
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const check = (): void => {
      if (cancelled) {
        return;
      }

      if (posthog.conversations.isAvailable()) {
        setIsPosthogChatEnabled(true);
      } else {
        setIsPosthogChatEnabled(false);
      }

      timeout = setTimeout(check, 3e3);
    };

    check();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [posthog]);

  // drift enabled hook
  useEffect(() => {
    if (!driftFeatureEnabled) {
      return;
    }
    let cancelled = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const check = (): void => {
      if (cancelled) {
        return;
      }

      if (window.drift) {
        setIsDriftLoaded(true);
      } else {
        setIsDriftLoaded(false);
      }

      timeout = setTimeout(check, 3e3);
    };

    check();

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [driftFeatureEnabled]);

  return { isChatEnabled, showChat };
};
